/** Level chooser: corpus size versus the learner's own progress, per JLPT level. */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ApiError, LEVELS, api, levelColor, type Level, type Overview } from '../api'
import { ErrorNote, Progressbar, Spinner, Stat } from '../components/ui'
import { useAuth } from '../store/auth'

const BLURB: Record<Level, string> = {
  N5: 'Beginner — the first ~100 kanji and the radicals everything else is built from.',
  N4: 'Elementary — everyday verbs, places and time words.',
  N3: 'Intermediate — the bridge level, where compounds start to dominate.',
  N2: 'Upper intermediate — newspaper and workplace vocabulary.',
  N1: 'Advanced — rare readings and the long tail.',
}

function errText(err: unknown): string {
  return err instanceof ApiError ? err.message : 'Could not reach the server.'
}

export default function LevelSelect() {
  const { user, setUser } = useAuth()
  const [overview, setOverview] = useState<Overview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState<Level | null>(null)

  // "Open map" / "Study" navigate away while the PATCH is still in flight, so
  // every state write past an await has to check we are still on screen.
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    let alive = true
    void (async () => {
      setLoading(true)
      setError(null)
      try {
        const data = await api.overview()
        if (alive) setOverview(data)
      } catch (err) {
        if (alive) setError(errText(err))
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [])

  // Tracks the most recently requested level so that if two PATCHes overlap, only
  // the last one is allowed to write back. Without this the client and server can
  // end up disagreeing about the target level.
  const wanted = useRef<Level | null>(null)

  const chooseLevel = useCallback(
    async (level: Level) => {
      if (user?.target_level === level) return
      wanted.current = level
      setSaving(level)
      setError(null)
      try {
        const updated = await api.updateMe({ target_level: level })
        if (wanted.current !== level) return // superseded by a later click
        // setUser lives on the provider above the router, so it is safe to call
        // even once this page has been replaced by the map.
        setUser(updated)
      } catch (err) {
        if (mounted.current && wanted.current === level) setError(errText(err))
      } finally {
        if (mounted.current && wanted.current === level) setSaving(null)
      }
    },
    [setUser, user?.target_level],
  )

  const byLevel = new Map((overview?.levels ?? []).map((s) => [s.level, s] as const))

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-5 py-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-bold text-slate-100">Choose a level</h1>
        <p className="text-sm text-ink-500">
          Opening a level also makes it your target, so the map and the study queue default to it.
        </p>
      </header>

      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && <Spinner label="Loading the corpus…" />}
      {saving && <Spinner label={`Making ${saving} your target level…`} />}

      {overview && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {LEVELS.map((level) => {
              const stat = byLevel.get(level) ?? { level, total: 0, seen: 0, mastered: 0 }
              const isTarget = user?.target_level === level
              const masteredPct = stat.total > 0 ? Math.round((stat.mastered / stat.total) * 100) : 0
              return (
                <section
                  key={level}
                  className="card flex flex-col gap-4 p-5"
                  style={isTarget ? { borderColor: `${levelColor[level]}66` } : undefined}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-3xl font-bold" style={{ color: levelColor[level] }}>
                        {level}
                      </div>
                      <p className="mt-1 text-xs leading-relaxed text-ink-500">{BLURB[level]}</p>
                    </div>
                    <button
                      type="button"
                      aria-pressed={isTarget}
                      disabled={saving !== null}
                      onClick={() => void chooseLevel(level)}
                      className={`btn shrink-0 px-2.5 py-1 text-[11px] ${
                        isTarget ? 'text-ink-950' : 'border border-ink-700 bg-ink-850 text-ink-500 hover:text-slate-200'
                      }`}
                      style={isTarget ? { background: levelColor[level] } : undefined}
                    >
                      {isTarget ? 'Target' : saving === level ? 'Saving…' : 'Set target'}
                    </button>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="rounded-lg bg-ink-850/70 py-1.5">
                      <div className="text-lg font-bold text-slate-100">{stat.total}</div>
                      <div className="text-[10px] uppercase tracking-wide text-ink-500">kanji</div>
                    </div>
                    <div className="rounded-lg bg-ink-850/70 py-1.5">
                      <div className="text-lg font-bold text-slate-100">{stat.seen}</div>
                      <div className="text-[10px] uppercase tracking-wide text-ink-500">seen</div>
                    </div>
                    <div className="rounded-lg bg-ink-850/70 py-1.5">
                      <div className="text-lg font-bold text-slate-100">{stat.mastered}</div>
                      <div className="text-[10px] uppercase tracking-wide text-ink-500">mastered</div>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Progressbar value={stat.mastered} max={stat.total} color={levelColor[level]} />
                    <div className="flex justify-between text-[11px] text-ink-500">
                      <span>{masteredPct}% mastered</span>
                      <span>
                        {stat.seen} of {stat.total} started
                      </span>
                    </div>
                  </div>

                  <div className="mt-auto flex gap-2">
                    <Link
                      to={`/map?level=${level}`}
                      onClick={() => void chooseLevel(level)}
                      className="btn-primary flex-1"
                    >
                      Open map
                    </Link>
                    <Link
                      to={`/learn?level=${level}`}
                      onClick={() => void chooseLevel(level)}
                      className="btn-ghost flex-1"
                    >
                      Study
                    </Link>
                  </div>
                </section>
              )
            })}
          </div>

          <section className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-500">In the dataset</h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Kanji" value={overview.total_kanji.toLocaleString()} hint="graded N5–N1" />
              <Stat
                label="Components"
                value={overview.total_components.toLocaleString()}
                hint="parts kanji are built from"
              />
              <Stat label="Radicals" value={overview.total_radicals.toLocaleString()} hint="classical indexes" />
              <Stat
                label="Links"
                value={overview.total_edges.toLocaleString()}
                hint="parent → part relationships"
              />
            </div>
          </section>
        </>
      )}
    </div>
  )
}
